import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { adminApi, apiError } from "@/lib/api";
import { FieldError, FormSummary, RequiredLegend, Req, focusFirstError, clean } from "@/components/landing/Field";

const build = (tpl, n) => (tpl && n.trim() ? tpl.replace("{n}", encodeURIComponent(n.trim().replace(/\s/g, ""))) : "");

export const ShipDialog = ({ order, open, onClose, onShipped }) => {
  const [carriers, setCarriers] = useState({});
  const [carrier, setCarrier] = useState("Colissimo");
  const [number, setNumber] = useState("");
  const [url, setUrl] = useState("");
  const [urlEdited, setUrlEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    adminApi.get("/carriers").then(({ data }) => setCarriers(data)).catch((e) => toast.error(apiError(e)));
    setCarrier(order.carrier || "Colissimo");
    setNumber(order.tracking_number || "");
    setUrl(order.tracking_url || "");
    setUrlEdited(!!order.tracking_url);
    setErrors({});
  }, [open, order]);

  useEffect(() => {
    if (!urlEdited) setUrl(build(carriers[carrier], number));
  }, [carrier, number, carriers, urlEdited]);

  const submit = async (e) => {
    e.preventDefault();
    const errs = clean({ tracking_number: !number.trim() && "Le numéro de suivi est obligatoire." });
    setErrors(errs);
    if (Object.keys(errs).length) return focusFirstError(e.currentTarget);
    setSaving(true);
    try {
      const { data } = await adminApi.post(`/orders/${order.order_id}/ship`, { carrier, tracking_number: number, tracking_url: url || null });
      if (data.email) data.email.ok ? toast.success("Commande expédiée — e-mail envoyé au client") : toast.error(`Expédiée, mais échec de l'e-mail : ${data.email.error}`);
      else toast.success("Informations de suivi mises à jour");
      onShipped(data.order);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="ship-dialog" className="sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Expédier la commande {order.order_id}</DialogTitle>
          <DialogDescription>Le client recevra un e-mail « Votre ClimNova est en route » avec son suivi.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          <RequiredLegend testId="ship-required-legend" />
          <label className="flex flex-col text-sm font-medium text-slate-700">
            <span>Transporteur <Req /></span>
            <select data-testid="ship-carrier-select" value={carrier} onChange={(e) => { setCarrier(e.target.value); setUrlEdited(false); }} className="field-input mt-1">
              {Object.keys(carriers).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
            <span>Numéro de suivi <Req /></span>
            <input data-testid="ship-tracking-number-input" value={number} aria-invalid={!!errors.tracking_number}
              onChange={(e) => { setNumber(e.target.value); setErrors({}); }} className="field-input" placeholder="ex. 6A12345678901" />
            <FieldError id="ship-tracking-number" error={errors.tracking_number} />
          </label>
          <label className="flex flex-col text-sm font-medium text-slate-700">Lien de suivi {carrier === "Autre" ? "(facultatif)" : "(généré automatiquement, modifiable)"}
            <input data-testid="ship-tracking-url-input" value={url} onChange={(e) => { setUrl(e.target.value); setUrlEdited(true); }} className="field-input mt-1 text-xs" placeholder="https://…" />
          </label>
          <FormSummary errors={errors} testId="ship-form-summary" />
          <button type="submit" disabled={saving} data-testid="ship-submit-button" className="btn-heat !py-2.5 disabled:opacity-50">
            {saving ? "Enregistrement…" : order.status === "payee" ? "Marquer expédiée et prévenir le client" : "Mettre à jour le suivi"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
